import { ChangeDetectionStrategy, Component, computed, effect, inject, model, output, signal, untracked } from '@angular/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { ESegmentedToggleComponent, ESegmentedToggleOption } from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { loadHdChartGlobalSettings, saveHdChartGlobalSettings } from '../bodygraph/chart-tabs/hd-chart-global-settings';
import { HD_BODYGRAPH_COLOR_CONFIG_PRESETS } from '../bodygraph/hd-bodygraph-color-config';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import type { HdBodygraphChartPreset } from '../bodygraph/hd-bodygraph.models';
import {
  HD_PRINT_COLOR_SCHEME_APP,
  HdChartPrintLayout,
  HdChartPrintNameMode,
  HdChartPrintOptions,
  hdChartPrintName,
} from './hd-chart-print-options';

/** De chart waarvoor de printdialoog openstaat; `null` = gesloten. */
export interface HdChartPrintDialogTarget {
  readonly preset: HdBodygraphChartPreset;
}

const T = 'humanDesign.chart.printDialog';

/**
 * Tussenstap vóór de PDF (wens Steven 2026-10-22): kleurenschema, naam in de kop, Basis of
 * Regulier, en de center-keynotes. De keuzes (behalve een eigen naam) blijven bewaard in de
 * gebruikersinstellingen, zodat de volgende print met dezelfde keuzes opent.
 */
@Component({
  selector: 'hd-chart-print-dialog',
  imports: [SharedModule, NgSelectModule, ESegmentedToggleComponent],
  templateUrl: './hd-chart-print-dialog.component.html',
  styleUrl: './hd-chart-print-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartPrintDialogComponent {
  readonly target = model<HdChartPrintDialogTarget | null>(null);
  readonly confirmed = output<{ preset: HdBodygraphChartPreset; options: HdChartPrintOptions }>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly colorScheme = signal(HD_PRINT_COLOR_SCHEME_APP);
  protected readonly nameMode = signal<HdChartPrintNameMode>('firstName');
  protected readonly name = signal('');
  protected readonly layout = signal<HdChartPrintLayout>('basic');
  protected readonly centerKeynotes = signal(false);

  constructor() {
    // Bij openen: de vorige keuzes terug, de naam opnieuw uit de chart van nu.
    effect(() => {
      const target = this.target();
      if (target == null) return;
      untracked(() => {
        const stored = loadHdChartGlobalSettings();
        const knownScheme = HD_BODYGRAPH_COLOR_CONFIG_PRESETS.some(preset => preset.id === stored.printColorScheme);
        this.colorScheme.set(knownScheme ? stored.printColorScheme : HD_PRINT_COLOR_SCHEME_APP);
        const layouts: readonly HdChartPrintLayout[] = ['basic', 'regular', 'mandala'];
        this.layout.set(layouts.includes(stored.printLayout) ? stored.printLayout : 'basic');
        this.centerKeynotes.set(stored.printCenterKeynotes === true);
        // Een eigen naam hoort bij één chart: opnieuw beginnen vanaf de volledige naam.
        const mode = stored.printNameMode === 'fullName' || stored.printNameMode === 'custom' ? stored.printNameMode : 'firstName';
        this.nameMode.set(mode);
        this.name.set(hdChartPrintName(mode === 'custom' ? 'fullName' : mode, target.preset.name, ''));
      });
    });
  }

  private t(key: string): string {
    return this.transloco.translate<string>(`${T}.${key}`, undefined, this.contentLang.effectiveLang());
  }

  protected readonly text = computed(() => {
    this.contentLang.effectiveLang();
    return {
      title: this.t('title'),
      colorScheme: this.t('colorScheme'),
      name: this.t('name'),
      namePlaceholder: this.t('namePlaceholder'),
      layout: this.t('layout'),
      centerKeynotes: this.t('centerKeynotes'),
      create: this.t('create'),
    };
  });

  protected readonly colorSchemeOptions = computed(() => {
    this.text();
    return [
      { id: HD_PRINT_COLOR_SCHEME_APP, label: this.t('colorSchemeApp') },
      ...HD_BODYGRAPH_COLOR_CONFIG_PRESETS.map(preset => ({ id: preset.id as string, label: preset.label })),
    ];
  });

  protected readonly nameModeOptions = computed<readonly ESegmentedToggleOption<HdChartPrintNameMode>[]>(() => {
    this.text();
    return [
      { value: 'firstName', label: this.t('nameFirst') },
      { value: 'fullName', label: this.t('nameFull') },
      { value: 'custom', label: this.t('nameCustom') },
    ];
  });

  protected readonly layoutOptions = computed<readonly ESegmentedToggleOption<HdChartPrintLayout>[]>(() => {
    this.text();
    return [
      { value: 'basic', label: this.t('layoutBasic'), sub: this.t('layoutBasicSub') },
      { value: 'regular', label: this.t('layoutRegular'), sub: this.t('layoutRegularSub') },
      { value: 'mandala', label: this.t('layoutMandala'), sub: this.t('layoutMandalaSub') },
    ];
  });

  protected get canCreate(): boolean {
    return this.name().trim().length > 0;
  }

  /** Voornaam of volledige naam vult het tekstveld; "Eigen naam" laat de tekst staan om aan te passen. */
  protected onNameModeChange(mode: HdChartPrintNameMode): void {
    const target = this.target();
    this.nameMode.set(mode);
    if (target != null && mode !== 'custom') {
      this.name.set(hdChartPrintName(mode, target.preset.name, ''));
    }
  }

  /** Zelf typen maakt er een eigen naam van, tenzij de tekst gelijk blijft aan de gekozen variant. */
  protected onNameInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const target = this.target();
    this.name.set(value);
    if (target != null && this.nameMode() !== 'custom' && value !== hdChartPrintName(this.nameMode(), target.preset.name, '')) {
      this.nameMode.set('custom');
    }
  }

  protected onCenterKeynotesChange(event: Event): void {
    this.centerKeynotes.set((event.target as HTMLInputElement).checked);
  }

  protected onOpenChange(open: boolean): void {
    if (!open) this.target.set(null);
  }

  protected submit(): void {
    const target = this.target();
    const name = this.name().trim();
    if (target == null || !name) return;

    const options: HdChartPrintOptions = {
      colorScheme: this.colorScheme(),
      nameMode: this.nameMode(),
      name,
      layout: this.layout(),
      // Keynotes bestaan enkel bij Basis: bij Regulier is er naast de LPS-grids geen plaats.
      centerKeynotes: this.layout() === 'basic' && this.centerKeynotes(),
    };
    saveHdChartGlobalSettings({
      ...loadHdChartGlobalSettings(),
      printColorScheme: options.colorScheme,
      printNameMode: options.nameMode,
      printLayout: options.layout,
      // De voorkeur voor Basis blijft bewaard, ook als er nu Regulier gekozen werd.
      printCenterKeynotes: this.centerKeynotes(),
    });
    this.confirmed.emit({ preset: target.preset, options });
    this.target.set(null);
  }
}
